import type { ReactElement } from 'react';

/**
 * GadiDesk — the product's mark.
 *
 * A key tag: the object every rental hands across the counter. Inside it, three
 * offset bars — rows on a booking calendar. No car, no steering wheel, no
 * speedometer, no pin.
 *
 * Exactly one bar is teal; everything else is ink. That rule is the identity,
 * so it lives here rather than at each call site.
 *
 * This is *our* brand, not the client's. It marks the tool the counter staff
 * use; the client's own name sits in the header, and their colours belong to
 * their public website.
 */

interface LogoProps {
  /** `mark` is the tag alone, for tight spaces. */
  readonly variant?: 'full' | 'mark';
  /** `inverse` is for the ink rail. */
  readonly tone?: 'ink' | 'inverse';
  readonly size?: number;
}

export function Logo({ variant = 'full', tone = 'ink', size = 26 }: LogoProps): ReactElement {
  const outline = tone === 'inverse' ? '#F5F4F2' : '#16191C';
  const bar = tone === 'inverse' ? '#F5F4F2' : '#16191C';
  // The teal lifts on a dark ground; the sheet specifies both values.
  const accent = tone === 'inverse' ? '#3E9C9C' : '#0B6E6E';

  const mark = (
    <svg
      viewBox="0 0 58 64"
      width={size}
      height={(size / 58) * 64}
      fill="none"
      aria-hidden="true"
      className="shrink-0"
    >
      <rect x="8" y="3" width="42" height="58" rx="8" stroke={outline} strokeWidth="3.4" />
      <circle cx="29" cy="15" r="3.6" stroke={outline} strokeWidth="3.4" />
      <rect x="16" y="28" width="26" height="6" rx="3" fill={bar} />
      <rect x="22" y="39" width="15" height="6" rx="3" fill={accent} />
      <rect x="16" y="50" width="20" height="6" rx="3" fill={bar} />
    </svg>
  );

  if (variant === 'mark') return mark;

  return (
    <span className="flex items-center gap-2.5">
      {mark}
      <span
        className={[
          'text-heading leading-none tracking-tight',
          tone === 'inverse' ? 'text-ink-inverse' : 'text-ink',
        ].join(' ')}
      >
        <span className="font-semibold">Gadi</span>
        <span className={tone === 'inverse' ? 'font-normal opacity-70' : 'font-normal text-ink-muted'}>
          Desk
        </span>
      </span>
    </span>
  );
}
